import { Checkbox } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';

import { gridDateTimeFormatter } from '@shared/commonFunctions';
import { dataGridCol } from '@shared/components/grid/common';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { SERVICES } from '@shared/constants/apiConfig';
import { Wrapper } from '@styles/CommonStyles';

import { ConfigurationTableModal } from './components/ConfigurationTableModal';
import { requestCreateConfiguration, requestUpdateConfiguration } from './requests';
import { IConfiguration, IGroupsTokens } from './types';

export const ConfigurationTable = () => {
  const { data: configurationTables, isValidating, error, mutate } = useSWR<IConfiguration[]>(`${SERVICES.CONFIGURATION}/configurations/all`);
  const { data: groupsTokens } = useSWR<IGroupsTokens>(`${SERVICES.CONFIGURATION}/configurations/groups-tokens`);

  const [currentConfiguration, setCurrentConfiguration] = useState<IConfiguration | null>(null);
  const [configuartionModalPosition, setConfiguartionModalPosition] = useState(0);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  const gridActions = {
    onEdit: (data: IConfiguration) => {
      setCurrentConfiguration(data);
      setIsEditMode(true);
      setConfiguartionModalPosition(prevState => prevState + 1);
    },
  };

  const handleChange = useCallback(async (checked: boolean, row: IConfiguration) => {
    const data = {...row, isActive: checked};
    await requestUpdateConfiguration(data);
    mutate();
  }, []);

  const isLoading = useMemo(() => (!configurationTables && !error) || isValidating, [configurationTables, error, isValidating]);

  const columns: GridColDef[] = useMemo(() => [
    dataGridCol('updatedDate', {
      type: 'dateTime',
      flex: 1,
      minWidth: 110,
      valueFormatter: gridDateTimeFormatter,
    }),
    dataGridCol('configurationGroup', {
      flex: 2,
      minWidth: 160,
      type: 'singleSelect',
      valueOptions: groupsTokens?.configurationGroups.sort(),
    }),
    dataGridCol('tokenName', {
      flex: 2,
      minWidth: 160,
      type: 'singleSelect',
      valueOptions: groupsTokens?.tokenNames.sort(),
    }),
    dataGridCol('value', {
      flex: 1,
      minWidth: 100,
    }),
    dataGridCol('isActive', {
      sortable: false,
      renderCell: ({ row }) => <Checkbox checked={row.isActive} onChange={(event) => handleChange(event.target.checked, row)} />,
    }),
  ], [groupsTokens?.tokenNames, groupsTokens?.configurationGroups]);

  const handleAddButtonClick = () => {
    setCurrentConfiguration(null);
    setConfiguartionModalPosition(prevState => prevState + 1);
    setIsEditMode(false);
  };

  const handleSubmit = async (data: IConfiguration) => {
    isEditMode
      ? await requestUpdateConfiguration(data).then(() => {
          mutate();
          setConfiguartionModalPosition(0);
        })
      : await requestCreateConfiguration(data).then(() => {
          mutate();
          setConfiguartionModalPosition(0);
        });
  };

  return (
    <Wrapper>
      <ConfigurationTableModal
        configuartionModalPosition={configuartionModalPosition}
        handleSubmit={handleSubmit}
        handleClose={() =>  setConfiguartionModalPosition(0)}
        currentConfiguration={currentConfiguration}
      />

      <PhoenixGrid
        autoHeight
        withHeader
        canNotCopy
        showClearAllFilter
        name="Configuration"
        pageSize={10}
        columns={columns}
        loading={isLoading}
        rows={isLoading ? [] : configurationTables || []}
        actions={gridActions}
        buttonTitle="Add configuration"
        onButtonClick={handleAddButtonClick}
      />
    </Wrapper>
  );
};
